/**
 * View that dynamically creates a set of <li> elements from a Netx.Menu model
 * @class
 */
import Base from '@netx/core/lib/netx-view';
import Template from '../templates/menuView.html';
import {
	FindLegacyAction,
	RegisterLegacyAction,
} from '@netx/core/legacyActionManager';

class MenuView extends Base {
	/** @type {boolean} */
	get isMenuView() {
		return this.constructor.__isMenuView__;
	}

	/** @override */
	constructor(opts = {}) {
		// don't add .loading classes to menus
		opts.loadingMethods = [];
		opts.template = opts.template || Template;
		super(opts);

		/**
		 * The 'actions' array is set by the instantiator
		 * @memberOf Netx.Views.MenuView
		 * @alias actions
		 * @type {array}
		 */
		this.actions = opts.actions || [];

		this.context = opts.context === true;
		this.menu = opts.menu;

		// Hey, listen
		this
			// OPTIMIZATION TODO: only listen when routes exist in menu, not like this
			.listenTo(dispatcher, 'hash:changed', this.setActiveClasses)
			.listenTo(dispatcher, 'app:close:menus', () => {
				if (this.$dropdown && this.$dropdown.hasClass('open')) {
					this.$dropdownToggle.click();
				}
			});
	}

	/** @override */
	cancel(e) {
		if (this.$dropdown && this.$dropdown.hasClass('open')) {
			this.$dropdownToggle.click();
		}
	}

	/**
	 * Instead of templates, this module renders its own html with javascript.
	 * @override
	 */
	doTemplate() {
		// TODO MENU TEMPLATE
		if (this.template === Template) {
			let html = '';
			let section = 0;
			// a recursive function to walk the actions array:
			const _toHtml = (item) => {
				if (_.isArray(item)) {
					// found a section
					html += '<div data-section="' + section + '">\n';
					section++;
					_.each(item, _toHtml); // recursion!
					html += '</div>\n';
				} else if (_.isObject(item)) {
					// found a submenu
					// I designed this to use generic objects for submenus, but Spence assumed Netx.Menu objects which makes sense too ... so now we support either.
					var label, actions;
					if (_.isFunction(item.get)) {
						// submenu is a Netx.Menu object
						label = item.get('label');
						actions = item.get('actions');
					} else {
						label = item.label;
						actions = item.actions;
					}

					html += '<li class="dropdown">\n';
					html +=
						'<a tabindex="0" class="dropdown-toggle" data-toggle="dropdown"><span>' +
						i18n.t(label) +
						'</span><span class="caret"></span></a>\n';
					html += '<ul class="dropdown-menu" role="list">\n';
					_.each(actions, _toHtml); // recursion!
					html += '</ul>\n';
				} else {
					// found an action
					// TODO: check performance of Find, and/or implement Netx.Actions differently now that we have Menus.
					let a = FindLegacyAction(item);
					if (!a) {
						//throw new Error("unknown action '" + item + "'");
						console.warn("unknown action '" + item + "'; not rendering");
					} else if (!a.fake) {
						html +=
							'<li class="nx-action" data-action="' +
							a.event +
							'"><a>' +
							i18n.t(a.label) +
							'</a></li>';
					}
				}
			};

			// walk the actions array, recursively:
			_.each(this.menu.get('actions'), _toHtml);

			// If our element is a div.dropdown, wrap with structure
			if (this.$el.filter('div.dropdown').length > 0) {
				// NOTE: data-toggle div is required by bootstrap.
				html =
					'<div class="dropdown-toggle" data-toggle="dropdown"></div><ul class="dropdown-menu" role="list">' +
					html +
					'</ul>';
			}

			return html;
		}
		// Custom template
		else {
			let depth = 0;
			// We need to strip the comment from the template (menu refactor when?!)
			let $template = $(this.template({}).replace(/^\n<!--.*-->\n/, ''));
			// First level
			let $html = $template.clone(); // <----- this line == 99% of (long) render time for this view.  Can we figure out how not to do this? -m-
			// Keep a copy of our current menu, untouched. So we can follow depth as needed.
			let $currentMenu = $template.clone();

			// Empty everything inside but the header
			$html.children().not('header').remove();
			// Add some needed/helpful attributes
			$html
				.not('header')
				.attr('role', 'menu')
				.attr('data-nx-menu-depth', depth);
			/**
			 * _findTarget
			 * Find target based on attribute name
			 * @function
			 *
			 * @param {object} $item jQuery object of element
			 * @param {string} attr The attribute to search for
			 *
			 * @returns {object} jQuery element sought (or false if not found)
			 */
			const _findTarget = ($item, attr) => {
				let $actionTarget = $item.attr(attr);
				if ($actionTarget) {
					return $item;
				} else {
					$actionTarget = $item.find('[' + attr + ']');
					if ($actionTarget.length) {
						return $actionTarget;
					}
				}
				return false;
			};
			/**
			 * _processItem
			 * Process provided item with provided action
			 * @function
			 *
			 * @param {object} $item jQuery object of element
			 * @param {object} action Action object
			 *
			 * @returns {object} Processed jQuery element
			 */
			const _processItem = ($item, action) => {
				if (action.fake) return;

				// Our element to target
				let $actionTarget = false;

				// Has data-action
				$actionTarget = _findTarget($item, 'data-action');
				if (action.event) {
					if ($actionTarget) {
						$actionTarget.attr('data-action', action.event);
					} else {
						// Nothing found
					}
				} else {
					if ($actionTarget) {
						// Only remove if a real data action has not been provided
						if ($actionTarget.attr('data-action') === '{{action}}') {
							$actionTarget.removeAttr('data-action').removeClass('nx-action');
						}
					} else {
						// Nothing found
					}
				}

				// Has route
				$actionTarget = _findTarget($item, 'href');
				if (action.route && !_.isFunction(action.route)) {
					if ($actionTarget) {
						const hash = action.route.slice(0, 1) === '#' ? '' : '#';
						$actionTarget.attr('href', hash + action.route);
					} else {
						// Nothing found
					}
				} else {
					if ($actionTarget) {
						// Only remove if a real href has not been provided
						if ($actionTarget.attr('href') === '{{route}}') {
							$actionTarget.removeAttr('href');
						}
					} else {
						// Nothing found
					}
				}

				// Has data-target
				$actionTarget = _findTarget($item, 'data-target');
				if (action.target) {
					if ($actionTarget) {
						$actionTarget.attr('data-target', action.target);
					} else {
						// Nothing found
					}
				} else {
					if ($actionTarget) {
						// Only remove if a real data target has not been provided
						if ($actionTarget.attr('data-target') === '{{target}}') {
							$actionTarget.removeAttr('data-target');
						}
					} else {
						// Nothing found
					}
				}

				// Has label
				if (action.label) {
					$item = $(
						$item[0].outerHTML.replace(/{{label}}/g, i18n.t(action.label)),
					);
				}
				// Remove label
				else {
					$item = $($item[0].outerHTML.replace(/{{label}}/g, ''));
				}

				// Has icon
				if (action.icon) {
					const icon =
						typeof action.icon === 'string'
							? `<i class="fa fa-fw fa-${action.icon}" aria-hidden="true"></i>`
							: action.icon || '';
					$item = $($item[0].outerHTML.replace(/{{icon}}/g, icon));
				}
				// Remove icon
				else {
					let customIcon = $item.find('i').attr('class');
					if (customIcon) {
						customIcon = customIcon.includes('{{icon}}');
					}
					if (customIcon) {
						$item.find('i').remove();
					} else {
						$item[0].innerHTML = $item[0].innerHTML.replace(/{{icon}}/g, '');
					}
				}

				return $item;
			};
			/**
			 * _toCustomHtml
			 * Create our custom HTML
			 * @function
			 *
			 * @param {*} item An array (section), an object (submenu) or a menu item
			 * @param {object} $_html The jQuery object to append to
			 */
			const _toCustomHtml = (item, $_html) => {
				let $item;
				// Section
				if (_.isArray(item)) {
					// If we have a current menu already get the item from there - else get it from the main node
					$item = $currentMenu
						? $currentMenu.children().not('header').first().clone()
						: $template.children().not('header').first().clone();
					// We just take the tag type used for items in the template and append with class 'divider'
					$_html.append(
						$('<li>', {
							class: 'divider',
						}),
					);
					const $section = $currentMenu.clone();
					// Empty everything inside except the header
					$section.children().not('header').remove();
					// Add some needed/helpful attributes
					$section
						.not('header')
						.attr('role', 'menu')
						.attr('data-nx-menu-depth', depth);
					//$_html.after( $section );
					$_html.append($section);
					// Loop over actions in array
					_.each(item, (_item) => {
						_toCustomHtml(_item, $section);
					});
				}
				// Submenu
				else if (_.isObject(item)) {
					depth++;

					let label, icon, actions;
					// Submenu is a Netx.Menu object
					if (_.isFunction(item.get)) {
						label = item.get('label');
						icon = item.get('icon');
						actions = item.get('actions');
					} else {
						label = item.label;
						icon = item.icon;
						actions = item.actions;
					}
					// If we have a current menu already get the item from there - else get it from the main node
					$item = $currentMenu
						? $currentMenu.children().not('header').first().clone()
						: $template.children().not('header').first().clone();
					// First lets get the submenu of the item - if it has one
					let $subMenu = $item.find('.sub-menu');
					// Look for a submenu elsewhere
					if (!$subMenu.length) {
						if ($currentMenu) $subMenu = $currentMenu.find('.sub-menu').first();
						if (!$subMenu.length) {
							$subMenu = $template.find('.sub-menu').first();
						}
					}
					// If submenu was provided in template
					if ($subMenu.length) {
						// Submenu to append to
						$subMenu = $subMenu.clone();
						// Keep a copy of our current menu, untouched.
						$currentMenu = $subMenu.clone();
						// It is now safe to empty out submenu
						$subMenu.children().not('header').remove();
						// Make sure we do not have a submenu element in the item still
						$item.find('.sub-menu').remove();
					}
					// No submenu provided - we will reuse the parent node
					else {
						// Keep a copy of our current menu, untouched.
						$currentMenu = $template.clone();
						// Submenu to append to
						$subMenu = $template.clone().addClass('sub-menu');
						$subMenu.children().not('header').remove();
					}
					// Add pertinent dropdown classes
					$subMenu
						.addClass('dropdown-menu')
						.attr('role', 'menu')
						.attr('data-nx-menu-depth', depth);
					// Process submenu
					$subMenu = _processItem($subMenu, {label: label});
					// Process the item
					$item = _processItem($item, {label: label, icon: icon});
					// Add dropdown classes to item
					$item
						.addClass('dropdown')
						.addClass('parent')
						.addClass('dropdown-submenu');

					// Check for <a>
					let $a = $item.find('a');
					if ($a.length) {
						$a.addClass('dropdown-toggle')
							.attr('data-toggle', 'dropdown')
							.attr('title', label);
					} else {
						$item
							.addClass('dropdown-toggle')
							.attr('data-toggle', 'dropdown')
							.attr('title', label); //tooltips for dropdowns
					}
					// Loop over actions within item
					_.each(actions, (item) => {
						_toCustomHtml(item, $subMenu);
					});
					// This stops the quirky behavior of append
					// If you append an element straight up to another element that is not unique enough it will append to all elements that are "similar"
					$_html.append($item.html($item.html() + $subMenu[0].outerHTML));
				}
				// Menu item
				else {
					// Get action object
					let action = FindLegacyAction(item);
					$item = $currentMenu
						? $currentMenu.children().not('header').first().clone()
						: $template.children().not('header').first().clone();
					if (!action) {
						console.warn('The action "' + item + '" has not been registered.');
						// add a minimal fake action, so we still trigger an event
						action = {event: item, fake: true};
						RegisterLegacyAction(action);
					}
					// Check for a current sub menu, otherwise get from main node
					// Make sure item does not have a submenu element in it
					$item.find('.sub-menu').remove();
					// Process item
					$item = _processItem($item, action);
					// Append item
					$_html.append($item);
				}
			};

			// Header
			var headerLabel = this.menu.get('label'),
				$header = $html.find('header').first();

			if ($header.length) {
				if (headerLabel) {
					$header.replaceWith(
						$($header[0].outerHTML.replace(/{{label}}/g, i18n.t(headerLabel))),
					);
				} else {
					$header.replaceWith(
						$($header[0].outerHTML.replace(/{{label}}/g, '')),
					);
				}
			}

			// Loop over main actions
			_.each(this.menu.get('actions'), (item) => {
				depth = 1;
				// Reset to main node
				$currentMenu = $template.clone();
				_toCustomHtml(item, $html);
			});

			// Clean up
			$currentMenu = $template = null;

			if (this.context) {
				return [
					'<div class="dropdown-toggle" data-toggle="dropdown"></div>',
					'<div class="dropdown-menu" data-nx-drilldown>',
					$html[0].outerHTML,
					'</div>',
				].join('');
			} else {
				return $html[0].outerHTML;
			}
		}
	}

	/** @override */
	postRender() {
		super.postRender();

		// Cache
		this.$dropdown = this.$('.dropdown:not(.dropdown-submenu)');
		// The dropdown could be this.el
		if (!this.$dropdown.length && this.$el.is('.dropdown')) {
			this.$dropdown = this.$el;
		}
		this.$dropdownToggle = this.$dropdown.find('.dropdown-toggle');
		this.$subMenus = this.$('.dropdown.dropdown-submenu');

		this.configureSubmenus();
	}

	/**
	 * Enable/disable of our menu is handled by the parent view,
	 * because the relevant el can be outside of our domain,
	 * but we can & do check our own submenus.
	 * @method
	 */
	configureSubmenus() {
		// Check submenus:
		_.each(this.$subMenus, (el) => {
			const $el = this.$(el);
			// TODO: see TODO in Netx.View.configureMenu() re: possible bug here.
			// Check if we have any enabled actions
			let allActions = $el.find('.nx-action');
			let disabledActions = $el.find('.nx-action[disabled]');

			// Maybe it is just href based and not data-action
			if (!allActions.length) {
				allActions = $el.find('[href]');
				disabledActions = $el.find('[href][disabled]');
			}

			// Deal with the dropdown toggle as well
			$el
				.siblings('[data-toggle="dropdown"]')
				.enableIf(disabledActions.length < allActions.length);
		});

		this.setActiveClasses();
	}

	/**
	 * @method
	 * @param {string} hash Current hash
	 * @param {string} lasthash Last hash
	 */
	setActiveClasses(hash, lasthash) {
		if (!hash) {
			hash = Backbone.history.location.hash;
		}
		//manage the active state of menu items]
		this.$('.active').removeClass('active');

		// look for parent routes, set active class on href or role="presentation"
		let $actionTarget, $tabPresentation;
		if (hash.lastIndexOf('/') > 0) {
			while (hash.lastIndexOf('/') > 0) {
				$actionTarget = this.$('a[href="' + _.escape(hash) + '"]');
				if ($actionTarget.length) {
					$tabPresentation = $actionTarget.closest('[role="presentation"]');
					($tabPresentation.length ? $tabPresentation : $actionTarget).addClass(
						'active',
					);
				}
				hash = hash.slice(0, hash.lastIndexOf('/'));
			}
		} else {
			$actionTarget = this.$('a[href="' + hash + '"]');
			$actionTarget.closest('[role="presentation"]').addClass('active');
		}
	}

	/**
	 * Sometimes this view is instantiated on an existing element; when we 'remove' this view, we want to preserve
	 * its outer element, so we can't call Backbone's remove() method.  Instead, we use removeInner().
	 * @todo: elevate to Netx.View?
	 * @method
	 */
	removeInner() {
		this.stopListening().undelegateEvents().$el.empty();
	}

	/** @type {boolean} */
	static get __isMenuView__() {
		return true;
	}

	/** @type {string} */
	static get __name__() {
		return 'MenuView';
	}
}

export default MenuView;
